import { useRef, useState } from "react";

interface ImageDropzoneProps {
  label: string;
  hint?: string;
  required?: boolean;
  error?: string;
  aspect?: "square" | "landscape";
  accept?: string;
  previewSrc?: string;
  onFile: (file: File) => void;
}

export default function ImageDropzone({
  label,
  hint,
  required,
  error,
  aspect = "square",
  accept = "image/*",
  previewSrc = "",
  onFile,
}: ImageDropzoneProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState("");
  const [fileName, setFileName] = useState("");
  const [isImage, setIsImage] = useState(true);
  const [dragging, setDragging] = useState(false);

  const handleFiles = (files: FileList | null) => {
    const file = files?.[0];
    if (!file) return;
    const imageFile = file.type.startsWith("image/");
    setIsImage(imageFile);
    setFileName(file.name);
    if (imageFile) {
      setPreview(URL.createObjectURL(file));
    } else {
      setPreview("");
    }
    onFile(file);
  };

  const isLandscape = aspect === "landscape";

  const dropzoneClass = `cursor-pointer rounded-box border-2 border-dashed transition-colors duration-200 p-4 ${
    dragging
      ? "border-primary-400 bg-primary-50"
      : error
        ? "border-red-300 bg-red-50"
        : "border-background-300 bg-background-50 hover:border-primary-300"
  }`;

  const icon = (
    <span className="w-9 h-9 flex items-center justify-center text-2xl">
      <i className={isImage ? "ri-image-add-line" : "ri-file-text-line"}></i>
    </span>
  );

  const text = fileName || "اسحب الملف هنا أو اضغط للاختيار";
  const subText = hint || "PNG أو JPG بحد أقصى 2 ميجابايت";
  const shownPreview = preview || previewSrc;

  return (
    <div>
      <label className="block text-sm font-medium text-foreground-800 mb-2">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <div
        onDragOver={(event) => {
          event.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(event) => {
          event.preventDefault();
          setDragging(false);
          handleFiles(event.dataTransfer.files);
        }}
        onClick={() => inputRef.current?.click()}
        className={dropzoneClass}
      >
        {isLandscape ? (
          <div className="flex flex-col gap-3">
            {shownPreview ? (
              <img
                src={shownPreview}
                alt={label}
                className="h-40 w-full rounded-btn object-cover"
              />
            ) : (
              <div className="h-40 w-full flex flex-col items-center justify-center gap-1.5 rounded-btn bg-white border border-background-200 text-foreground-400">
                {icon}
                <span className="text-sm font-medium text-foreground-600">
                  {text}
                </span>
              </div>
            )}
            <div className="text-center">
              {shownPreview && (
                <p className="text-sm font-medium text-foreground-800">
                  {fileName || "الصورة الحالية"}
                </p>
              )}
              <p className="text-xs text-foreground-400">{subText}</p>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-4">
            {shownPreview ? (
              <img
                src={shownPreview}
                alt={label}
                className="w-28 h-28 rounded-btn object-cover shrink-0"
              />
            ) : (
              <div className="w-28 h-28 shrink-0 flex items-center justify-center rounded-btn bg-white border border-background-200 text-foreground-400">
                {icon}
              </div>
            )}
            <div className="min-w-0">
              <p className="text-sm font-medium text-foreground-800">
                {fileName || "اسحب الملف هنا أو اضغط للاختيار"}
              </p>
              <p className="mt-1 text-xs text-foreground-400">{subText}</p>
            </div>
          </div>
        )}
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          className="hidden"
          onChange={(event) => handleFiles(event.target.files)}
        />
      </div>
      {error && <p className="mt-1.5 text-xs text-red-500">{error}</p>}
    </div>
  );
}